import { useQuery, useQueryClient } from '@tanstack/react-query';
import Page from '../../../components/PageWrapper';
import { IPremiumApplicantDTO, ITableFilterItem } from '../../../resources/interfaces';
import { useEffect, useMemo, useState } from 'react';
import GTable, { GTableActionColumn, ITablePaginationData } from '../../../components/elements/GTable/GTable';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { archivePremiumFinancingApplication, getAllPremiumFinancedApplicants } from '../../../api/requests';
import PopOnMe from '../../../components/elements/poponme';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import useUserAccount from '../../../hooks/userAccountHook';
import { capitalizeSentence, showToast } from '../../../utils/miscFunctions';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import useRoute from '../../../hooks/routesHook';
import useModal from '../../../hooks/modalHook';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import EditReviewStage from './EditReviewStage';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import ActionConfirmation from '../../../components/elements/ActionConfirmation';
import usePremiumFinancing from '../../../hooks/premiumFinancingHook';
import { COMPANY_TYPES, PREMIUM_FINANCING_REVIEW_STATES } from '../../../resources/enums';
import Button from '../../../components/form-control/Button';
import { Archive, Banknote, Building, Edit } from 'lucide-react';
import { PremiumFinancing } from '../../../models/financing/PremiumFinancing';
import FinancingOverviewPanel from './FinancingOverviewPanel';
import { SelectedDateRange } from '../../../components/dashboard/date-range-selector/datePicker';
import usePartner from '../../../hooks/partnerHook';
import SelectDropdown from '../../../components/form-control/SelectDropdown';
import { Company } from '../../../models/company/Company';
import React from 'react';

interface ICellOriginal {
  row: { original: PremiumFinancing };
}

const Financing = () => {
  const { user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const [showPerPage, setShowPerPage] = useState<number>(25);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { getPremiumFinancingQuote, FinancingModal } = usePremiumFinancing();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [currentPage, setCurrentPage] = useState<number>(
    searchParams.get('page') ? Number(searchParams.get('page')) : 0
  );
  const to = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.to;
  const from = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.from;
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { fetchPremiumFinancingMetrics } = usePremiumFinancing();
  const { fetchAllCompaniesByTypes } = usePartner();
  const [selectedDateRange, setSelectedDateRange] = useState<any>({
    from: null,
    to: null
  });

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => fetchAllCompaniesByTypes([COMPANY_TYPES.INSURER]),
    enabled: !!user.isOwner()
  });

  const companyId = !user?.isInternalAdmin() ? user?.company?.id : undefined
  const [selectedCompany, setSelectedCompany] = useState<any>();

  const { data: premiumFinancingMetrics, isLoading: isMetricLoading } = useQuery({
    queryKey: ['fetchPremiumFinancingMetrics', selectedCompany, selectedDateRange],
    queryFn: () => fetchPremiumFinancingMetrics(selectedCompany, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !!selectedCompany
  })

  const { data: mixedPremiumFinancingMetrics } = useQuery({
    queryKey: ['fetchPremiumFinancingMetrics'],
    queryFn: () => fetchPremiumFinancingMetrics(selectedCompany, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !selectedCompany && user.isInternalAdmin()
  })

  const displayFinancedApplicants = async () => {
    try {
      const response: any = await  getAllPremiumFinancedApplicants(currentPage, showPerPage, countryId, from, to, selectedCompany);
      return response;
    } catch (error) {
      console.log(error);
    }
    return
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['displayFinancedApplicants'] });
  };

  const archiveSelectedApplication = (financedApplicant: any) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="archive"
          type='warning'
          resourceType={''}
          resourceName={''}
          onClose={closeModal}
          onConfirm={() => {
            archivePremiumFinancingApplication(financedApplicant.id)
              .then((response: any) => {
                closeModal();
                showToast.success(response?.message);
                invalidateQueries();
              })
              .catch((error: any) => {
                console.log(error);
                closeModal();
                showToast.error(error.description);
              });
          }}
        />
      ),
      title: 'Archive Application',
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
    });
  }

  const getTableRowActions = (financedApplication: any) => {
    const actions = [];

    actions.push({
      label: 'Archive',
      icon: <Archive className='h-4 w-4' />,
      callback: () => {
        archiveSelectedApplication(financedApplication)
      },
      // data: data
    },
    );
    if (user.company?.type !== COMPANY_TYPES.FINANCIER && financedApplication.reviewStage !== PREMIUM_FINANCING_REVIEW_STATES.APPROVED) {
      actions.push({
        label: 'Edit',
        icon: <Edit className="h-4 w-4" />,
        callback: () => { navigate(`${getDashboardBase()}/premium-financing/${financedApplication.applicationNumber}/edit`) },
        // data: data
      });
    }



    return actions;
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'APPLICATION ID',
        accessor: 'applicationNumber',
        Cell: (row: any) => {
          return (
            <Link
              to={`${getDashboardBase()}/premium-financing/${row.row?.original?.applicationNumber}`}
              // target="_blank"
              className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
              {row.cell.value ? row.cell.value : '-'}
            </Link>
          );
        }
      },
      {
        Header: 'CUSTOMER',
        accessor: 'customerName',
        Cell: (row: any) => {
          return (
            <span>
              {row.cell.value ? `${row.cell.value}` : '-'}
            </span>
          );
        }
      },
      {
        Header: 'FINANCED AMOUNT',
        accessor: 'premium',
        Cell: (row: any) => {
          return (
            <div className='flex flex-col'>
              <span>{selectedCountry?.currencySymbol} {row.cell.value ? row.cell.value : '-'}</span>
              {/* <span className='text-xs text-gray-500'>{row?.row?.original?.plan?.frequency ? row?.row?.original?.plan?.frequency  : '-'}</span> */}
            </div>
          );
        }
      },
      {
        Header: 'STATUS',
        accessor: 'reviewStage',
        Cell: (row: any) => {
          return (
            <PolicyStatus
              status={
                row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'
              }
              classes="px-2  w-full"
            />
          );
        }
      },
      {
        Header: 'FINANCIER',
        accessor: 'financierName',
        Cell: (row: any) => {
          return (
            <span>{row.cell.value ? row.cell.value : '-'}</span>
          );
        }
      },
      {
        Header: 'UNDERWRITER',
        accessor: 'insurerName',
        Cell: (row: any) => {
          return <span>{row.cell.value ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'ACTION',
        id: 'ACTION',
        Cell: (row: ICellOriginal) => {
          return (
          <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ];

    return [
      {
        Header: 'M',
        columns: baseColumns
      }
    ];
  }, []);

  const { data: applicants, isLoading } = useQuery({
    queryKey: [
      'displayFinancedApplicants',
      countryId,
      searchParams,
      currentPage,
      showPerPage,
      from,
      to,
      selectedCompany,
    ],
    queryFn: displayFinancedApplicants,
    enabled: !!selectedCompany || (!selectedCompany && user.isInternalAdmin()),
    staleTime: 1000 * 10,
    placeholderData: (prev) => prev,
    gcTime: 1000 * 60 * 30 * 5,
    select: (response) => ({
      allApplicants: response?.data?.content,
      pagination: {
        per_page: response?.data.size,
        total: response?.data.totalElements,
        totalPages: response?.data.totalPages,
        from: response?.data.pageable?.offset + 1,
        to:
          response?.data?.totalElements >
          response?.data?.pageable?.offset + showPerPage
            ? response?.data?.pageable?.offset + showPerPage
            : response?.data?.totalElements,
      },
    }),
  });

  const pagination = applicants?.pagination;
  const allApplicants = React.useMemo(() => {
      return applicants?.allApplicants?.map((_financing: any) =>  new PremiumFinancing(_financing));
  }, [applicants?.allApplicants]);

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'create_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select start date',
        label: 'Date Range'
      }
    ];

    return filters;
  }

  const getOtherActions = () => {
    let actions = [];

    if (user.isManager() || user.isAdministrator()) {
      actions.push({
        label: 'Export sales',
        callback: () => { }
      });
    }

    return actions;
  };

  useEffect(() => {
    setSelectedCompany(companyId);
  }, [companyId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page title="Premium Financing" className="">
      <div className="p-5 min-h-screen bg-white dark:bg-darkBg rounded-lg shadow-md">
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between flex-wrap gap-5">

            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                <h2 className="font-semibold text-3xl dark:text-darkBg-mainText">Premium Financing</h2>

              </div>
              <p className="text-sm dark:text-darkBg-subText">View a list of all premium financing applications.</p>
            </div>

            <div className="flex gap-5 justify-end pt-2 md:pt-0 items-center">
              {(user?.isInternalAdmin()) && (
                <SelectDropdown
                  list={companies?.map((company: Company) => ({
                    name: company.name,
                    value: company.id,
                  }))}
                  placeholder='Select Company'
                  listSelectedValue={selectedCompany}
                  prepend={<Building size={16} className="text-gray-500 mr-2" />}
                  onChange={(value: string) => setSelectedCompany(Number(value))}
                />
              )}

              {((user.isAdministrator() || user.isOwner() || user.isInsurer() || user.isAgency() || user.isBroker()) && user.isInternalAdmin()) && !user?.isFinancier() &&
                <Button
                  bgColor="primary"
                  label={'New Application'}
                  icon={<Banknote size={26} className='mr-2' />}
                  onClick={() => navigate(`${getDashboardBase()}/premium-financing/application-creation`)}
                />
              }
            </div>
          </div>

          <div className="mt-6 space-y-6">
            <FinancingOverviewPanel
              data={{
                totalApplications: premiumFinancingMetrics?.totalApplications || mixedPremiumFinancingMetrics?.totalApplications || 0,
                totalFinancingAmount: premiumFinancingMetrics?.totalFinancingAmount || mixedPremiumFinancingMetrics?.totalFinancingAmount || 0,
                approvedFinancingAmount: premiumFinancingMetrics?.approvedFinancingAmount || mixedPremiumFinancingMetrics?.approvedFinancingAmount || 0,
                pendingApplications: premiumFinancingMetrics?.pendingApplications || mixedPremiumFinancingMetrics?.pendingApplications || 0,
                rejectedApplications: premiumFinancingMetrics?.rejectedApplications || mixedPremiumFinancingMetrics?.rejectedApplications || 0,
                approvedApplications: premiumFinancingMetrics?.approvedApplications || mixedPremiumFinancingMetrics?.approvedApplications || 0,
                statusDistribution: premiumFinancingMetrics?.statusDistribution || mixedPremiumFinancingMetrics?.statusDistribution || [],
                approvalRate: premiumFinancingMetrics?.approvalRate || mixedPremiumFinancingMetrics?.approvalRate || 0,
                inApprovalFinancingAmount: premiumFinancingMetrics?.inApprovalFinancingAmount || mixedPremiumFinancingMetrics?.inApprovalFinancingAmount || 0,
                inApprovalApplications: premiumFinancingMetrics?.inApprovalApplications || mixedPremiumFinancingMetrics?.inApprovalApplications || 0,
                inReviewApplications: premiumFinancingMetrics?.inReviewApplications || mixedPremiumFinancingMetrics?.inReviewApplications || 0,
                inReviewFinancingAmount: premiumFinancingMetrics?.inReviewFinancingAmount || mixedPremiumFinancingMetrics?.inReviewFinancingAmount || 0,
                isMetricLoading: isMetricLoading
              }}
              setSelectedDateRange={(data: SelectedDateRange) => setSelectedDateRange(data)}
            />

            <div className="mt-6">
              <GTable
                columns={columns}
                data={allApplicants ?? []}
                classes="h-fit"
                isRowLinked={false}
                tableType={'Application(s)'}
                showSearch={false}
                searchPlaceholder='Search by name or application id'
                loading={isLoading}
                paginationData={pagination}
                onPageChange={handlePageChange}
                showPerPage={showPerPage}
                onShowPerPageChange={(page: number) => setShowPerPage(page)}
                actionElement={
                  getOtherActions().length > 0 && (
                    <PopOnMe actions={getOtherActions()}>
                      <div className="cursor-pointer">
                        <EllipsisVerticalIcon className="h-6 w-6 " />
                      </div>
                    </PopOnMe>
                  )
                }
                showFilter={
                  <>
                    <TableFilter
                      openFilter={openFilter}
                      onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                      selectedFilters={tableFilters}
                      onRemoveFilter={(filter: ITableFilterItem, index: number) => {
                        const _tableFilters: ITableFilterItem[] = [...tableFilters];
                        let thisFilter = { ...tableFilters[index] };
                        thisFilter = { ..._tableFilters[index], value: '' };
                        _tableFilters[index] = thisFilter;
                        setTableFilters([..._tableFilters]);
                      }}
                      content={
                        <TableFilterForm
                          filters={tableFilters}
                          getValues={(filters: ITableFilterItem[]) => {
                            setTableFilters(filters);
                            setOpenFilter(false);
                          }}
                          onResetFilters={() => setTableFilters(getDefaultFilters())}
                        />
                      }
                    />
                  </>
                }
              />
            </div>
          </div>
        </div>
      </div>
      <FinancingModal />
      <ModalComponent />
    </Page>
  );
};

export default Financing;
